import React from "react";

import {
  MentionAccessDialog,
  type MentionAccessVariant,
} from "#app/components/issue-view/activity/mention-access-dialog";
import { createDialogEmitter } from "#app/lib/dialog-emitter";
import { useMentionAccessDialog } from "#app/lib/hooks/use-mention-access-dialog";
import type { Omittable } from "@salestrak/ui";

// Global emitter so any consumer that adds people to an issue
// (note composer's @-mentions, assignee value changes, future
// auto-add flows, …) can trigger the "they don't have access" prompt
// without each call site owning its own dialog state + lookup hook.
//
// Each "open" carries the issue id and the candidate user ids; the
// mount component runs the access check and only opens the dialog
// when one or more candidates actually lack `view`. Action paths
// (do nothing / grant item / grant workflow) post-process the same
// list and dismiss.

type PromptArgs = Omittable<{
  issueId: string;
  userIds: string[];
  // Team principals (Teams-property assignments) — probed via
  // teamsWithoutItemAccess and granted through the team share mutations.
  teamIds?: string[];
  // Drives the grant-button visibility. The viewer's effective
  // access on the issue (must be full_edit to manage sharing).
  viewerCanManageSharing: boolean;
  // Copy variant — "mention" (default) for note @-mentions,
  // "assignment" for assignee additions.
  variant?: MentionAccessVariant;
}>;

// A request bus, not an open flag: each `open` carries the candidates, and
// the mount decides whether anything shows after its access lookup.
const mentionAccessPrompt = createDialogEmitter<PromptArgs>("mention_access");

export function useMentionAccessActions() {
  return React.useMemo(
    () => ({ promptIfMissing: mentionAccessPrompt.open }),
    [],
  );
}

// Single mount inside [workspace]/_layout.tsx. Owns dialog state and
// runs the missing-access lookup + grant mutations (via
// useMentionAccessDialog) on behalf of every promptIfMissing caller.
export function MentionAccessDialogMount() {
  const {
    pending,
    setPending,
    submitting,
    close,
    lookupMissing,
    grantItemAccess,
    grantWorkflowAccess,
  } = useMentionAccessDialog();

  React.useEffect(() => {
    const onPrompt = async (args: PromptArgs) => {
      if (!args.userIds.length && !args.teamIds?.length) return;
      try {
        const missing = await lookupMissing(
          args.issueId,
          args.userIds,
          args.teamIds ?? [],
        );
        if (missing.users.length === 0 && missing.teams.length === 0) return;
        setPending({
          issueId: args.issueId,
          users: missing.users,
          teams: missing.teams,
          canManageSharing: args.viewerCanManageSharing,
          variant: args.variant ?? "mention",
        });
      } catch (error) {
        // Background — log and drop. The originating action has
        // already happened; failing to show the grant prompt
        // shouldn't disrupt it.
        console.error("Failed to check mention access:", error);
      }
    };
    return mentionAccessPrompt.onOpen((args) => {
      void onPrompt(args);
    });
  }, [lookupMissing, setPending]);

  return (
    <MentionAccessDialog
      open={pending != null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      users={pending?.users ?? []}
      teams={pending?.teams ?? []}
      variant={pending?.variant ?? "mention"}
      canManageSharing={pending?.canManageSharing ?? false}
      submitting={submitting}
      onDoNothing={close}
      onGrantItemAccess={grantItemAccess}
      onGrantWorkflowAccess={grantWorkflowAccess}
    />
  );
}
